import { message as antdMessage } from 'antd';
import React, { memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import CopyableText from '@fmbt/ui/CopyableText';
import { LoadingSpinnerDiv } from '@fmbt/ui/LoadingSpinner';
import DrawerSimpleMap from 'components/composites/DrawerSimpleMap';
import NewTabLink from 'components/composites/NewTabLink';
import SensorAboutTab, { ContentItem } from 'components/composites/SensorAboutTab';
import { EM_DASH } from 'constants/index';
import useAssetType from 'store/modules/assetTypes/hooks';
import { useCountry } from 'store/modules/countries/hooks';
import { useEnterpriseCountry } from 'store/modules/enterprise/hooks';
import { ephemeralGetRequest } from 'store/modules/ephemeralRequest/actions';
import { AssetTypeCode } from 'types/models/asset-type';
import { BackOfficeDeviceMapDevicePointDetails } from 'types/models/back-office-device-map-point';
import { DeviceId } from 'types/models/device';
import constructEnterpriseDomain from 'utils/construct-enterprise-domain';
import { formatCoordinates } from 'utils/format-coordinates';
import { RAW_TO_PARSED_ASSET_TYPE_CODE } from 'utils/SamplePoints/asset-types/parse-raw-asset-types';

interface BackOfficeDeviceMapDeviceDetailsProps {
  deviceId: DeviceId;
}

function BackOfficeDeviceMapDeviceDetails({ deviceId }: BackOfficeDeviceMapDeviceDetailsProps) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(true);
  const [devicePoint, setDevicePoint] = React.useState<BackOfficeDeviceMapDevicePointDetails>();

  const country = useEnterpriseCountry();
  const countryObj = useCountry(country);

  useEffect(() => {
    (async () => {
      try {
        const devicePointData = await new Promise<BackOfficeDeviceMapDevicePointDetails>((resolve, reject) => {
          setIsLoading(true);
          dispatch(
            ephemeralGetRequest<BackOfficeDeviceMapDevicePointDetails>(
              `device-map/points/${deviceId}`,
              resolve,
              reject
            )
          );
        });

        setDevicePoint(devicePointData);
      } catch (error) {
        antdMessage.error(
          `Error loading device detail: ${error instanceof Error ? error.message : error}`
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [deviceId, dispatch]);

  if (isLoading) return <LoadingSpinnerDiv />;
  if (!devicePoint || !countryObj) return <></>;

  const renderEnterpriseContent = () => {
    const enterpriseLink = constructEnterpriseDomain({ subdomain: devicePoint.enterpriseSubdomain });
    const enterpriseName = devicePoint.enterpriseName || EM_DASH;

    return (
      <NewTabLink href={enterpriseLink} text={enterpriseName} iconSize={22} />
    );
  };

  const renderOwnerContent = () => {
    const ownerName = devicePoint.ownerName || EM_DASH;
    const ownerEmail = devicePoint.ownerEmail;
    const ownerPhone = devicePoint.ownerPhone;
    return (
      <div className="vertical-gap-8">
        <div className="body-copy">
          <span className="body-emphasis-copy">Name: </span>
          {ownerName}
        </div>
        <div className="body-copy">
          <span className="body-emphasis-copy">Email: </span>
          {ownerEmail ? <CopyableText>{ownerEmail}</CopyableText> : EM_DASH}
        </div>
        <div className="body-copy">
          <span className="body-emphasis-copy">Phone: </span>
          {ownerPhone ? <CopyableText>{ownerPhone}</CopyableText> : EM_DASH}
        </div>
      </div>
    );
  };

  const AssetCount = ({ assetTypeId, count }: { assetTypeId: AssetTypeCode, count: number }) => {
    const assetName = useAssetType(assetTypeId).name;
    return (
      <div className="body-copy">
        <span className="body-emphasis-copy">{assetName}: </span>
        {count}
      </div>
    );
  };

  const renderSensorCountContent = () => {
    const assetCounts = devicePoint.assetCounts || [];

    if (assetCounts.length === 0) {
      return 'No sensors connected';
    }

    return (
      <div className="vertical-gap-8">
        {assetCounts
          .map((asset, index) => {
            const { assetTypeId, count } = asset;
            const supportedAssetTypeId = RAW_TO_PARSED_ASSET_TYPE_CODE[assetTypeId];
            if (!supportedAssetTypeId) return null;

            return (
              <AssetCount
                key={index}
                assetTypeId={supportedAssetTypeId}
                count={count}
              />
            );
          })
          .filter(Boolean)}
      </div>
    );
  };

  const renderDeviceLocationContent = (): ContentItem['content'] => {
    if (!devicePoint.lat || !devicePoint.lng) {
      return EM_DASH;
    }

    return <CopyableText>{formatCoordinates([devicePoint.lat, devicePoint.lng])}</CopyableText>;
  };

  const contentItems: ContentItem[] = [
    {
      label: 'Serial Number',
      content: devicePoint.serialNumber || EM_DASH
    },
    {
      label: 'Name',
      content: devicePoint.name || EM_DASH
    },
    {
      label: 'Device Location',
      content: renderDeviceLocationContent()
    },
    {
      label: 'Enterprise',
      content: renderEnterpriseContent()
    },
    {
      label: 'Owner',
      content: renderOwnerContent()
    },
    {
      label: 'Sensor Count',
      content: renderSensorCountContent()
    }
  ];

  return (
    <>
      <SensorAboutTab contentItems={contentItems} />
      <DrawerSimpleMap
        location={[devicePoint.lat, devicePoint.lng]}
        country={countryObj}
      />
    </>
  );
}

export default memo(BackOfficeDeviceMapDeviceDetails);